import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Label } from "@/components/ui/label";
import { Clock, Save } from "lucide-react";
import { toast } from "sonner";

const DAYS = ["Dom", "Seg", "Ter", "Qua", "Qui", "Sex", "Sáb"];

export default function StoreHoursSettings() {
  const [hours, setHours] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings").then(({ data }) => {
      setHours(data.hours);
      setIsOpen(data.is_open);
    }).catch(() => {});
  }, []);

  if (!hours) return null;

  const toggleDay = (d) =>
    setHours((h) => ({ ...h, days: h.days.includes(d) ? h.days.filter((x) => x !== d) : [...h.days, d] }));

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/admin/hours", hours);
      setHours(data.hours);
      setIsOpen(data.is_open);
      toast.success("Horário de funcionamento salvo!");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao salvar o horário.");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "bg-[#0C0A09] border border-[#292524] text-stone-100 rounded-md px-3 py-2 text-sm";

  return (
    <div className="max-w-2xl mb-6" data-testid="store-hours-settings">
      <div className="bg-[#1C1917] border border-[#292524] rounded-xl p-4">
        <div className="flex items-center justify-between mb-1">
          <p className="text-sm font-semibold flex items-center gap-2">
            <Clock className="w-4 h-4" /> Horário de funcionamento
          </p>
          <span data-testid="store-status-badge" className={`text-xs font-semibold px-2 py-0.5 rounded-full ${isOpen ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400"}`}>
            {isOpen ? "Aberto agora" : "Fechado agora"}
          </span>
        </div>
        <p className="text-xs text-stone-500 mb-3">
          Fora deste horário o site mostra "Fechado" e não aceita pedidos. Se o fechamento for depois da meia-noite, o pedido continua liberado até a hora de fechar.
        </p>
        <label className="flex items-center gap-2 text-sm mb-3 cursor-pointer">
          <input type="checkbox" data-testid="hours-enabled-checkbox" checked={hours.enabled} onChange={(e) => setHours((h) => ({ ...h, enabled: e.target.checked }))} />
          Ativar controle de horário
        </label>
        <div className="flex flex-wrap gap-2 mb-3">
          {DAYS.map((name, d) => (
            <button key={d} type="button" onClick={() => toggleDay(d)} data-testid={`hours-day-${d}`}
              className={`px-3 py-1.5 rounded-full text-xs border transition-colors ${hours.days.includes(d) ? "bg-primary text-primary-foreground border-primary" : "border-[#292524] text-stone-400 hover:text-white"}`}>
              {name}
            </button>
          ))}
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <Label className="text-stone-300">Abre às</Label>
            <input type="time" data-testid="hours-open-input" value={hours.open} onChange={(e) => setHours((h) => ({ ...h, open: e.target.value }))} className={`${inputCls} block`} />
          </div>
          <div>
            <Label className="text-stone-300">Fecha às</Label>
            <input type="time" data-testid="hours-close-input" value={hours.close} onChange={(e) => setHours((h) => ({ ...h, close: e.target.value }))} className={`${inputCls} block`} />
          </div>
          <button onClick={save} disabled={saving} data-testid="save-hours-btn"
            className="inline-flex items-center gap-1.5 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60">
            <Save className="w-4 h-4" /> {saving ? "Salvando..." : "Salvar"}
          </button>
        </div>
      </div>
    </div>
  );
}
